/**
 * Display names for values that arrive from the API.
 *
 * These are *data*, not UI copy: activity type names live in the backend, and
 * admins can also create activities with free-text names. So they cannot be
 * translated the way `$t('some.key')` copy is.
 *
 * Two things make this workable:
 *
 *   1. The API is sent an `Accept-Language` header (see apiManager.js). If the
 *      backend grows per-locale names, it can return them and this module
 *      becomes a no-op — that is the proper long-term fix.
 *   2. Until then, the shared catalogue of standard activity types is mapped
 *      below under `activityTypes.*`. Anything not in the catalogue — including
 *      every admin-created name — falls through unchanged, so nothing is ever
 *      mangled or blanked.
 *
 * To cover more of the catalogue, add a slug under `activityTypes` in all three
 * files in src/locales. No code change needed here.
 */

import i18n from './index'

/** "Water Aerobics" -> "water_aerobics", so names map to stable key slugs. */
const toSlug = name => String(name)
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')

/**
 * @param {string} name the name as returned by the API
 * @returns {string} a translated name when the value is a known standard type,
 *                   otherwise `name` untouched
 */
export const translateActivityType = name => {
  if (!name) return ''
  const key = `activityTypes.${toSlug(name)}`
  // `te` checks the key exists before translating; without it a miss would
  // render the raw key ("activityTypes.morning_yoga") to the user.
  return i18n.te(key) ? i18n.t(key) : name
}

/**
 * Display label for a system registration field ("First Name", "Date of Birth").
 *
 * Like activity types, the English name here is a *contract*, not copy: it is
 * sent to the API as `name`, used to match against MANDATORY_CATALOG, and built
 * into DOM ids. So the stored value stays English and only the rendered label is
 * translated. Admin-created custom fields fall through unchanged.
 *
 * @param {string} name the field's canonical (English) name
 * @returns {string} the translated label, or `name` if it is not a system field
 */
export const systemFieldLabel = name => {
  if (!name) return ''
  const key = `systemFields.${toSlug(name)}`
  return i18n.te(key) ? i18n.t(key) : name
}

/**
 * Display label for a package type ("Session", "Membership").
 *
 * Same shape as activity types: the value comes from
 * /corporate-admin/packages/listPackageTypes and is referenced elsewhere by id,
 * so only the rendered label is translated. Anything outside the catalogue falls
 * through unchanged.
 *
 * @param {string} name the type name as returned by the API
 */
export const packageTypeName = name => {
  if (!name) return ''
  const key = `packageTypes.${toSlug(name)}`
  return i18n.te(key) ? i18n.t(key) : name
}

/**
 * Display label for a challenge type ("Core Point", "Steps", "Pushups").
 *
 * From /challenge-category-types. Same rule as the other API vocabularies: the
 * id/slug is what the app stores and submits, so only the label is translated
 * and unknown types pass through unchanged.
 *
 * @param {string} name the type label as returned by the API
 */
export const challengeTypeName = name => {
  if (!name) return ''
  const key = `challengeTypes.${toSlug(name)}`
  return i18n.te(key) ? i18n.t(key) : name
}

export default {
  translateActivityType, systemFieldLabel, packageTypeName, challengeTypeName,
}
